/**
 * Authenticated API access — the ONLY way the app should call the backend.
 *
 * Every request carries an App Bridge SESSION TOKEN (a short-lived JWT
 * Shopify signs for the embedded app) in the Authorization header. The
 * backend verifies it and takes the acting shop from the token — the old
 * `?shop=` query param is ignored there, so don't append it.
 *
 * Usage:
 *   import { apiFetch } from '../lib/api';
 *   const res = await apiFetch('/api/notifications');          // GET
 *   const res = await apiFetch('/api/ads/generate', { method: 'POST', ... });
 *
 * apiFetch/authenticatedFetch return the raw Response (callers keep their
 * existing res.ok / res.json() handling). apiGet/apiPost/apiPut/apiDelete
 * are thin conveniences that set method + JSON headers.
 */

import { PLAN_LIMIT_EVENT, USAGE_CHANGED_EVENT, QUOTA_CAP_CODES, emitQuotaEvent } from './quotaEvents';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SHOP_STORAGE_KEY = 'autonoma_shop';

function resolveShop() {
  try { return window.localStorage.getItem(SHOP_STORAGE_KEY); } catch { return null; }
}

/**
 * A fresh session token for the current request, from the LATEST
 * (CDN-loaded, auto-updating) App Bridge — public/index.html loads
 * https://cdn.shopify.com/shopifycloud/app-bridge.js which exposes
 * window.shopify. idToken() is fetched on EVERY call — tokens live
 * ~60s, so caching one is a footgun. Null when the app isn't running
 * inside the Shopify admin (the backend will then 401 and we bounce
 * through the install flow).
 */
export async function getFreshSessionToken() {
  try {
    if (typeof window !== 'undefined' && window.shopify?.idToken) {
      return await window.shopify.idToken();
    }
  } catch (err) {
    console.warn('App Bridge idToken failed:', err.message);
  }
  return null;
}

// ── Global connectivity / API-state events ───────────────────────────
// authenticatedFetch raises these so the shell (AppLayout) can show ONE
// banner/toast instead of ~60 call sites each remembering to.
export const API_OFFLINE_EVENT = 'autonoma:offline';
export const API_ONLINE_EVENT = 'autonoma:online';
export const API_RATE_LIMITED_EVENT = 'autonoma:rate-limited';
export const SESSION_LOST_EVENT = 'autonoma:session-lost';
export const ACCESS_CHANGED_EVENT = 'autonoma:access-changed';

function emit(name, detail) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* SSR/test */ }
}

let offline = false;
function markOffline(err) {
  if (offline) return;
  offline = true;
  emit(API_OFFLINE_EVENT, { message: err?.message });
}
function markOnline() {
  if (!offline) return;
  offline = false;
  emit(API_ONLINE_EVENT);
}

/**
 * Merchant-facing text for a thrown fetch/parse error. Raw messages like
 * "Failed to fetch" / "Unexpected token < in JSON" never reach the UI.
 */
export function humanizeError(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;
  if (err.name === 'AbortError' || err.isTimeout) return 'This is taking longer than expected. Please try again.';
  if (err instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(err.message || '')) {
    return "We couldn't reach Autonoma — check your connection and try again.";
  }
  if (err instanceof SyntaxError || /unexpected token|json/i.test(err.message || '')) {
    return 'Autonoma returned an unexpected response. Please try again in a moment.';
  }
  if (/^server error: 5\d\d$/i.test(err.message || '') || err.status >= 500) {
    return 'Autonoma hit a server error. Please try again — if it keeps happening, contact support.';
  }
  return err.message || fallback;
}

// Only ever kick off ONE reinstall redirect at a time, no matter how many
// parallel requests hit 401 together. The flag RESETS after a while: a
// blocked top-navigation (Chromium refuses it without a user gesture and
// does NOT throw) used to leave every later 401 silently ignored for the
// rest of the session.
let redirectingToInstall = false;

function redirectToInstall() {
  if (redirectingToInstall) return;
  redirectingToInstall = true;
  setTimeout(() => { redirectingToInstall = false; }, 15000);
  const shop = resolveShop();
  const url = shop
    ? `${API_URL}/api/auth/install?shop=${encodeURIComponent(shop)}`
    : `${API_URL}/api/auth/install`;
  // Tell the shell first so a visible "Reconnect" CTA exists even when
  // the automatic escape is blocked.
  emit(SESSION_LOST_EVENT, { reinstallUrl: url });
  // OAuth must run at the TOP window — inside the admin iframe the
  // redirect has to escape the frame. App Bridge patches window.open so
  // `_top` is the supported escape; the direct top-location write is the
  // fallback for non-embedded loads.
  try {
    if (window.top && window.top !== window.self) {
      const opened = window.open(url, '_top');
      if (opened !== null) return;
      window.top.location.href = url;
      return;
    }
  } catch { /* cross-origin top — fall through */ }
  window.location.assign(url);
}

/** Default per-request timeout. Reads (GET) get one automatically; writes
 * only when the caller passes `timeoutMs` (generation kick-offs return
 * fast, but chat streaming and sync image edits legitimately run long). */
const DEFAULT_GET_TIMEOUT_MS = 45000;

/**
 * fetch() with the App Bridge session token attached.
 *
 *   - Prefixes `path` with the API base URL.
 *   - On a 401 the token may simply have expired mid-flight → retry ONCE
 *     with a freshly minted token.
 *   - Still 401 after the retry → the shop's OAuth session is gone;
 *     redirect to the install flow (once).
 */
export async function authenticatedFetch(path, init = {}) {
  const { timeoutMs, ...fetchInit } = init;
  const method = String(fetchInit.method || 'GET').toUpperCase();
  const effectiveTimeout = timeoutMs != null ? timeoutMs : (method === 'GET' ? DEFAULT_GET_TIMEOUT_MS : 0);

  const run = async () => {
    const token = await getFreshSessionToken();
    const headers = new Headers(fetchInit.headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    // Timeout: only when the caller didn't bring their own signal.
    let signal = fetchInit.signal;
    let timer = null;
    if (!signal && effectiveTimeout > 0 && typeof AbortController !== 'undefined') {
      const ctrl = new AbortController();
      signal = ctrl.signal;
      timer = setTimeout(() => ctrl.abort(), effectiveTimeout);
    }
    try {
      const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...fetchInit, headers, signal });
      markOnline();
      return res;
    } catch (err) {
      if (err?.name === 'AbortError' && timer) {
        const e = new Error('Request timed out');
        e.name = 'AbortError';
        e.isTimeout = true;
        throw e;
      }
      // fetch() only rejects for network-level failures (offline, DNS,
      // CORS, connection reset) — never for HTTP status codes.
      if (err instanceof TypeError) markOffline(err);
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  let res = await run();
  if (res.status !== 401) return observe(res, method);

  res = await run();
  if (res.status !== 401) return observe(res, method);

  // Confirm it's genuinely our auth saying no (not some other 401 shape)
  // before bouncing the merchant through OAuth.
  try {
    const body = await res.clone().json();
    if (body?.error === 'invalid_session_token' || body?.error === 'Not authenticated') {
      redirectToInstall();
    }
  } catch { /* non-JSON 401 — leave it to the caller */ }
  return res;
}

/**
 * Cross-cutting status handling that call sites can't forget:
 *   402 → the shop's plan/billing state changed under us (subscription
 *         lapsed, plan never picked) → tell useAccess to re-poll now.
 *   403 + upgrade + quota cap code → the server (the authority) says the
 *         monthly allowance can't cover this → the plan overlay opens
 *         right here, whatever the call site does with the error.
 *   429 → generation cap (code generation_limit_reached) is handled by
 *         the call sites' throwIfGenerationLimit; plain rate limits
 *         raise a one-line "slow down" toast.
 * The response body is only peeked via clone() so callers still read it.
 */
export const JOB_STARTED_EVENT = 'autonoma:job-started';

// One overlay per burst: a batch submit can 403 several times at once.
let lastPlanLimitAt = 0;
function planLimitFromResponse(res) {
  res.clone().json().then((body) => {
    if (!body || body.upgrade !== true || !QUOTA_CAP_CODES.includes(body.code)) return;
    const now = Date.now();
    if (now - lastPlanLimitAt < 800) return;
    lastPlanLimitAt = now;
    emitQuotaEvent(PLAN_LIMIT_EVENT, { feature: body.feature || null, code: body.code, message: body.error || null, source: 'server' });
    emitQuotaEvent(USAGE_CHANGED_EVENT, {});
  }).catch(() => {});
}

function observe(res, method = 'GET') {
  if (res.status === 402) {
    emit(ACCESS_CHANGED_EVENT, { status: 402 });
  } else if (res.status === 403) {
    planLimitFromResponse(res);
  } else if (res.ok && method !== 'GET' && /application\/json/i.test(res.headers.get('content-type') || '')) {
    // A mutation that returned a job → the progress pill polls right now
    // instead of on its next (up to 15s) tick. JSON only: never tee a
    // streaming (chat SSE) body. A started job also means the usage
    // numbers moved — the pre-check cache must not say otherwise.
    res.clone().json().then((body) => {
      if (body && (body.job || body.jobId || body.batchId)) {
        emit(JOB_STARTED_EVENT, { jobId: body.jobId || body.job?.id });
        emitQuotaEvent(USAGE_CHANGED_EVENT, {});
      }
    }).catch(() => {});
  } else if (res.status === 429) {
    res.clone().json().then((body) => {
      if (body?.code === 'generation_limit_reached') return; // call-site toast
      const retryAfter = Number(res.headers.get('Retry-After')) || null;
      emit(API_RATE_LIMITED_EVENT, { retryAfter, message: body?.error });
    }).catch(() => emit(API_RATE_LIMITED_EVENT, {}));
  }
  return res;
}

/** Test hook: run the cross-cutting status handling on a fake response. */
export const __observeForTests = observe;

/** Short alias used across the app. */
export const apiFetch = authenticatedFetch;

const jsonInit = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

export const apiGet = (path) => authenticatedFetch(path);
export const apiPost = (path, body) => authenticatedFetch(path, jsonInit('POST', body));
export const apiPut = (path, body) => authenticatedFetch(path, jsonInit('PUT', body));
export const apiDelete = (path) => authenticatedFetch(path, { method: 'DELETE' });

export default apiFetch;
